"use client";

import { c as _c } from "react/compiler-runtime";
import { Loader2 } from "lucide-react";
import { useIsHappeningNow } from "~/hooks/events/useIsHappeningNow";
import { eventTimeZone, formatEventDateRange, formatEventLocation, formatEventStart } from "~/lib/events/driver-events";
import { cn } from "~/lib/utils";
import type { DriverEventDay } from "~/server/api/routers/events";
import { EventThumbnail } from "./EventThumbnail";

/**
 * One event day, as a card in the strip under the hero search card.
 *
 * Sized for where it sits. The hero has to hold the headline, the search card,
 * this strip and the subtitle above the fold on a laptop, so the artwork band is
 * short and the text lines are tight — but it is a poster card, not a list row,
 * because four of them in a row read as a set of things to choose between.
 *
 * Shows only what the events feed actually carries: artwork, category, the day's
 * date and start time, and the venue. There is deliberately no price and no spot
 * count — the backend exposes neither to a driver surface, and the one
 * price-shaped field on the response (`event_base_price_minor`) is the pricing
 * engine's internal floor, which is why it never leaves the router (see
 * `DriverEvent`).
 *
 * Dates and times are the *venue's*, not the viewer's: the feed is nationwide.
 */
export function EventCard(t0) {
  const $ = _c(22);
  const {
    day,
    onOpen,
    busy: t1,
    disabled: t2
  } = t0;
  const busy = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  const {
    event
  } = day;
  const timeZone = eventTimeZone(event);
  let t3;
  if ($[0] !== event) {
    t3 = formatEventLocation(event);
    $[0] = event;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const location = t3;
  const live = useIsHappeningNow(day.startsAt, day.endsAt);
  let t4;
  if ($[2] !== day || $[3] !== onOpen) {
    t4 = () => onOpen(day);
    $[2] = day;
    $[3] = onOpen;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = (disabled || busy) && "cursor-default hover:translate-y-0";
  let t6;
  if ($[5] !== t5) {
    t6 = cn("group flex h-full flex-col overflow-hidden rounded-(--r-lg) border border-border bg-surface text-left", "transition-all duration-200 hover:-translate-y-0.5 hover:border-(--blue) hover:shadow-(--shadow-md)", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--blue) focus-visible:ring-offset-2", t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== event) {
    t7 = <EventThumbnail event={event} className="h-[84px] w-full" showCategory={true} />;
    $[7] = event;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== busy) {
    t8 = busy && <span className="absolute inset-0 flex items-center justify-center bg-white/60"><Loader2 className="h-4 w-4 animate-spin text-(--blue)" /></span>;
    $[9] = busy;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <span className="relative block">{t7}{t8}</span>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== live) {
    t10 = live && <span className="rounded-(--r-full) bg-(--red-bg) px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.07em] text-(--red-text)">Now</span>;
    $[14] = live;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== event.name) {
    t11 = <span className="line-clamp-2 min-h-[2.75em] break-words text-[13px] font-bold leading-snug text-body">{event.name}</span>;
    $[16] = event.name;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== location) {
    t12 = <span className="min-h-[1.25em] truncate text-[11px] leading-tight text-muted">{location}</span>;
    $[18] = location;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = "mt-auto truncate pt-0.5 text-[11px] text-(--ink-40)";
  const t14 = formatEventStart(day.startsAt, timeZone);
  let t15;
  if ($[20] !== t14) {
    t15 = <span className={t13}>{t14}</span>;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  return <button type="button" onClick={t4} disabled={disabled || busy} aria-busy={busy} className={t6}>{t9}<span className="flex flex-1 flex-col gap-1 p-3"><span className="flex flex-wrap items-center gap-1.5"><span className="rounded-(--r-sm) bg-(--blue-pale) px-1.5 py-0.5 text-[10.5px] font-bold text-accent-dark">{formatEventDateRange(day.startsAt, day.endsAt, timeZone)}</span>{t10}</span>{t11}{t12}{t15}</span></button>;
}